import axios from 'axios';

// API base URL configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export interface KPIEnrichmentData {
  id?: number;
  kpiCode: string;
  name: string;
  category: string;
  subcategory?: string;
  units: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

class KPIEnrichmentService {
  // Get all KPI enrichments
  async getAllEnrichments(): Promise<KPIEnrichmentData[]> {
    try {
      console.log('DEBUG KPI ENRICHMENT SERVICE: Fetching all KPI enrichments - API Call: GET /api/kpi-enrichments');
      
      const response = await axios.get(`${API_BASE_URL}/kpi-enrichments`);
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Fetched ${response.data.data?.length || 0} enrichments`);
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching KPI enrichments:', error);
      return [];
    }
  }

  // Get KPI enrichment by code
  async getEnrichmentByCode(kpiCode: string): Promise<KPIEnrichmentData | null> {
    try {
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Fetching enrichment for code ${kpiCode} - API Call: GET /api/kpi-enrichments/code/${kpiCode}`);
      
      const response = await axios.get(`${API_BASE_URL}/kpi-enrichments/code/${encodeURIComponent(kpiCode)}`);
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      
      if (response.data.success && response.data.data) {
        console.log(`DEBUG KPI ENRICHMENT SERVICE: Found enrichment for ${kpiCode}:`, response.data.data);
        return response.data.data;
      }
      
      return null;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        console.log(`DEBUG KPI ENRICHMENT SERVICE: No enrichment found for code ${kpiCode}`);
        return null;
      }
      console.error(`Error fetching KPI enrichment for code ${kpiCode}:`, error);
      return null;
    }
  }

  // Get multiple KPI enrichments by codes
  async getEnrichmentsByCodes(kpiCodes: string[]): Promise<KPIEnrichmentData[]> {
    try {
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Fetching enrichments for codes ${kpiCodes.join(', ')} - API Call: POST /api/kpi-enrichments/by-codes`);
      
      const response = await axios.post(`${API_BASE_URL}/kpi-enrichments/by-codes`, { codes: kpiCodes });
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Found ${response.data.data?.length || 0} enrichments`);
      
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching KPI enrichments by codes:', error);
      return [];
    }
  }

  // Create a new KPI enrichment
  async createEnrichment(enrichment: Omit<KPIEnrichmentData, 'id' | 'createdAt' | 'updatedAt'>): Promise<KPIEnrichmentData | null> {
    try {
      console.log('DEBUG KPI ENRICHMENT SERVICE: Creating new KPI enrichment - API Call: POST /api/kpi-enrichments');
      console.log('DEBUG KPI ENRICHMENT SERVICE: Request payload:', JSON.stringify(enrichment, null, 2));
      
      const response = await axios.post(`${API_BASE_URL}/kpi-enrichments`, enrichment);
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Created enrichment with ID: ${response.data.data?.id}`);
      
      return response.data.data;
    } catch (error) {
      console.error('Error creating KPI enrichment:', error);
      return null;
    }
  }

  // Update an existing KPI enrichment
  async updateEnrichment(id: number, enrichment: Partial<Omit<KPIEnrichmentData, 'id' | 'createdAt' | 'updatedAt'>>): Promise<KPIEnrichmentData | null> {
    try {
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Updating KPI enrichment ${id} - API Call: PUT /api/kpi-enrichments/${id}`);
      console.log('DEBUG KPI ENRICHMENT SERVICE: Update payload:', JSON.stringify(enrichment, null, 2));
      
      const response = await axios.put(`${API_BASE_URL}/kpi-enrichments/${id}`, enrichment);
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      
      return response.data.data;
    } catch (error) {
      console.error(`Error updating KPI enrichment with ID ${id}:`, error);
      return null;
    }
  }

  // Save or update KPI enrichment (upsert by code)
  async saveEnrichment(enrichment: Omit<KPIEnrichmentData, 'id' | 'createdAt' | 'updatedAt'>): Promise<KPIEnrichmentData | null> {
    try {
      // First check if enrichment already exists
      const existing = await this.getEnrichmentByCode(enrichment.kpiCode);
      
      if (existing && existing.id) {
        // Update existing enrichment
        console.log(`DEBUG KPI ENRICHMENT SERVICE: Updating existing enrichment for code ${enrichment.kpiCode}`);
        return await this.updateEnrichment(existing.id, enrichment);
      } else {
        // Create new enrichment
        console.log(`DEBUG KPI ENRICHMENT SERVICE: Creating new enrichment for code ${enrichment.kpiCode}`);
        return await this.createEnrichment(enrichment);
      }
    } catch (error) {
      console.error('Error saving KPI enrichment:', error);
      return null;
    }
  }

  // Bulk save KPI enrichments
  async bulkSaveEnrichments(enrichments: Omit<KPIEnrichmentData, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<{
    created: number;
    updated: number;
    errors: string[];
  }> {
    try {
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Bulk saving ${enrichments.length} KPI enrichments - API Call: POST /api/kpi-enrichments/bulk-enrich`);
      console.log('DEBUG KPI ENRICHMENT SERVICE: Bulk payload:', JSON.stringify(enrichments, null, 2));
      
      const response = await axios.post(`${API_BASE_URL}/kpi-enrichments/bulk-enrich`, enrichments);
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Bulk result:`, response.data.data);
      
      return response.data.data || { created: 0, updated: 0, errors: [] };
    } catch (error) {
      console.error('Error bulk saving KPI enrichments:', error);
      return { created: 0, updated: 0, errors: ['Failed to save enrichments'] };
    }
  }

  // Delete a KPI enrichment
  async deleteEnrichment(id: number): Promise<boolean> {
    try {
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Deleting KPI enrichment ${id} - API Call: DELETE /api/kpi-enrichments/${id}`);
      
      const response = await axios.delete(`${API_BASE_URL}/kpi-enrichments/${id}`);
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      
      return response.data.success;
    } catch (error) {
      console.error(`Error deleting KPI enrichment with ID ${id}:`, error);
      return false;
    }
  }

  // Clear all KPI enrichments (for testing)
  async clearAllEnrichments(): Promise<{ deletedCount: number }> {
    try {
      console.log('DEBUG KPI ENRICHMENT SERVICE: Clearing all KPI enrichments - API Call: DELETE /api/kpi-enrichments/clear-all');
      
      const response = await axios.delete(`${API_BASE_URL}/kpi-enrichments/clear-all`);
      
      console.log(`DEBUG KPI ENRICHMENT SERVICE: API Response Status: ${response.status}`);
      console.log(`DEBUG KPI ENRICHMENT SERVICE: Deleted ${response.data.data?.deletedCount || 0} enrichments`);
      
      return { deletedCount: response.data.data?.deletedCount || 0 };
    } catch (error) {
      console.error('Error clearing all KPI enrichments:', error);
      return { deletedCount: 0 };
    }
  }
}

export default new KPIEnrichmentService(); 